import { reactive, toRefs, computed } from 'vue';
import type { UnwrapRef } from 'vue';

type MutationFn<TPayload, TResponse> = (payload: TPayload) => Promise<TResponse>;

interface MutationState<TData = unknown, TError = unknown> {
	data: TData | null;
	error: TError | null;
	status: number | null;
	isError: boolean;
	isSuccess: boolean;
	isPending: boolean;
}

interface UseMutationOptions<TPayload = any, TData = any, TError = any> {
	mutationFn?: MutationFn<TPayload, TResponse<TData>>;
	onSuccess?: (data: TData, success: boolean, status: number) => void;
	onPending?: () => void;
	onError?: (error: TError) => void;
	onSettled?: (data: TData | null, error: TError | null) => void;
}

interface TResponse<TData> {
	success: boolean;
	status: number;
	data: TData;
	[key: string]: any;
}

export function useMutation<TPayload = any, TData = any, TError = any>(options: UseMutationOptions<TPayload, TData, TError> = {}) {
	const { mutationFn = async () => ({} as TResponse<TData>), onSuccess = () => {}, onPending = () => {}, onError = () => {}, onSettled = () => {} } = options;

	const state = reactive<MutationState<TData, TError>>({
		data: null,
		error: null,
		status: null,
		isError: false,
		isSuccess: false,
		isPending: false,
	});

	const mutate = async (payload: TPayload): Promise<void> => {
		if (state.isPending) return;
		try {
			state.isPending = true;
			onPending();

			const response = await mutationFn(payload);
			if (!response) return;

			state.isSuccess = response.success;
			state.data = response.data as UnwrapRef<TData>;
			state.status = response.status;

			onSuccess(response.data, response.success, response.status);
			return response?.data?.data;
		} catch (error) {
			state.error = error as UnwrapRef<TError>;
			state.isError = true;
			onError(state.error as TError);
			console.error(error);
		} finally {
			state.isPending = false;
			onSettled(state.data as TData, state.error as TError);
		}
	};

	const rateLimit = computed(() => (state.error as any)?.rateLimit);

	return {
		mutate,
		rateLimit,
		...toRefs(state),
	};
}
export default useMutation;
